/**
 * Hook and utility for acquiring access tokens
 *
 * Provides both a React hook and a standalone function for getting
 * access tokens from MSAL for API calls.
 */
import { useMsal } from '@azure/msal-react'
import { useCallback } from 'react'
import { msalInstance } from './AuthProvider'
import { loginRequest, isInternalDomain } from './msalConfig'

// Check if auth is bypassed for local development
const isLocalDevelopmentBypass =
  process.env.REACT_APP_API_X_USERNAME &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1')

// MSAL is only used for internal domains
// External domains use oauth2-proxy-ciam which injects the token at infrastructure level
const shouldUseMsal = isInternalDomain() && !isLocalDevelopmentBypass

/**
 * Module-level reference counter: how many acquireTokenRedirect calls are
 * currently in flight.
 *
 * A boolean flag was previously used, but it was not safe when two callers
 * both fail silent acquisition at the same time. The second caller's
 * acquireTokenRedirect would be rejected by MSAL ("interaction already in
 * progress"), its catch block would reset the flag to false, and
 * AuthProvider would then treat the first caller's still-active redirect as
 * non-interactive — re-rendering the app subtree exactly as if no redirect
 * were happening.
 *
 * A counter fixes this: each caller owns its own increment/decrement pair,
 * so a failing secondary call decrements back to 1 (not 0) when a primary
 * redirect is still active. The guard is positive when any redirect is in
 * flight, regardless of how many concurrent callers there are.
 */
let redirectInProgressCount = 0

export const getIsInteractiveAcquireTokenInProgress = () =>
  redirectInProgressCount > 0

/**
 * Get access token silently (for use outside React components)
 *
 * This function can be called from anywhere (e.g., request.js utility)
 * to get the current access token.
 *
 * @returns {Promise<string|null>} Access token or null if not authenticated
 */
export const getAccessToken = async () => {
  // Skip token acquisition if MSAL is not used:
  // - Local development bypass
  // - External domains (oauth2-proxy-ciam injects token)
  if (!shouldUseMsal || !msalInstance) {
    return null
  }

  try {
    const accounts = msalInstance.getAllAccounts()

    if (accounts.length === 0) {
      console.warn('No accounts found - user may need to login')
      return null
    }

    const silentRequest = {
      ...loginRequest,
      account: accounts[0],
    }

    const response = await msalInstance.acquireTokenSilent(silentRequest)
    return response.idToken
  } catch (error) {
    console.error('Error acquiring token silently:', error)

    // If silent token acquisition fails, fall back to interactive redirect.
    // Increment the counter so AuthProvider treats AcquireToken as interactive.
    // Guard: if another redirect is already running (counter > 0), MSAL would
    // reject a second acquireTokenRedirect with "interaction already in
    // progress". Skip the attempt to avoid that error clearing the counter
    // owned by the already-active redirect.
    if (redirectInProgressCount > 0) {
      return null
    }
    try {
      redirectInProgressCount += 1
      const response = await msalInstance.acquireTokenRedirect(loginRequest)
      redirectInProgressCount -= 1
      return response?.accessToken || null
    } catch (interactiveError) {
      redirectInProgressCount -= 1
      console.error('Error acquiring token interactively:', interactiveError)
      return null
    }
  }
}

/**
 * React hook for getting access token
 *
 * Use this in React components that need to make authenticated API calls.
 *
 * @returns {Function} Async function that returns the access token
 */
export const useAccessToken = () => {
  const { instance, accounts } = useMsal()

  const acquireToken = useCallback(async () => {
    if (accounts.length === 0) {
      console.warn('No accounts found')
      return null
    }

    try {
      const silentRequest = {
        ...loginRequest,
        account: accounts[0],
      }

      const response = await instance.acquireTokenSilent(silentRequest)
      return response.idToken
    } catch (error) {
      console.error('Error acquiring token:', error)

      // Fallback to interactive redirect. Increment the counter so
      // AuthProvider treats AcquireToken as interactive while the redirect
      // is in progress (preventing a spurious app remount on silent refreshes).
      // Guard: if another redirect is already running, skip to avoid MSAL's
      // "interaction already in progress" error resetting the active counter.
      if (redirectInProgressCount > 0) {
        return null
      }
      try {
        redirectInProgressCount += 1
        await instance.acquireTokenRedirect(loginRequest)
        // acquireTokenRedirect navigates away; this line is never reached in
        // the success case, but decrement defensively on any early return.
        redirectInProgressCount -= 1
        return null
      } catch (redirectError) {
        redirectInProgressCount -= 1
        console.error('Redirect error:', redirectError)
        return null
      }
    }
  }, [instance, accounts])

  return acquireToken
}

export default useAccessToken
